import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, HeartHandshake, TrendingUp, Sparkles, Compass, Target } from 'lucide-react';

export default function NosotrosPage() {
  const valores = [
    {
      numero: '01',
      titulo: 'Cercanía',
      icon: HeartHandshake,
      descripcion:
        'Comunicación constante y transparente con cada cliente. Ofrecemos un trato humano, amable y personalizado para asegurar que cada envío se gestione con la máxima atención desde su origen hasta su destino.'
    },
    {
      numero: '02',
      titulo: 'Profesionalidad',
      icon: ShieldCheck,
      descripcion:
        'Actuamos con total responsabilidad, puntualidad y eficiencia en cada entrega. Garantizamos la seguridad de los paquetes y procesos ágiles que respaldan la confianza de quienes nos eligen día a día.'
    },
    {
      numero: '03',
      titulo: 'Sostenibilidad',
      icon: TrendingUp,
      descripcion:
        'Optimizamos continuamente nuestros procesos y recursos operativos para garantizar un servicio eficiente, seguro y duradero. Fomentamos relaciones sólidas y de confianza a largo plazo con nuestros clientes y aliados estratégicos.'
    }
  ];

  return (
    <div className="min-h-screen py-8 sm:py-12 px-10 sm:px-16 lg:px-8 max-w-7xl mx-auto flex flex-col items-center">
      
      {/* =========================================================================
          SECCIÓN HERO: VISIÓN (IZQUIERDA) Y MISIÓN (DERECHA)
          ========================================================================= */}
      <section className="w-full pt-2 sm:pt-4 pb-12 sm:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 justify-items-center w-full max-w-5xl mx-auto">
          
          {/* VISIÓN (IZQUIERDA) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full flex justify-center"
          >
            <div className="mv-outer group">
              <div className="mv-dot" />
              <div className="mv-card">
                <div className="mv-ray" />
                <div className="mv-line mv-topl" />
                <div className="mv-line mv-bottoml" />
                <div className="mv-line mv-leftl" />
                <div className="mv-line mv-rightl" />

                {/* CONTENIDO VISIÓN */}
                <div className="relative z-10 flex flex-col items-center justify-center h-full max-w-md mx-auto">
                  <div className="p-3 rounded-2xl bg-[#CE010B]/10 dark:bg-[#CE010B]/25 text-[#CE010B] dark:text-[#ff4d4d] border border-[#CE010B]/20 shadow-sm mb-4 transition-all duration-300 group-hover:scale-110 group-hover:bg-[#CE010B] group-hover:text-white">
                    <Compass className="w-7 h-7" />
                  </div>
                  <h2 
                    className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#CE010B] dark:text-[#ff3b30] tracking-tight mb-4 drop-shadow-sm"
                    style={{ fontFamily: "'Secular One', sans-serif" }}
                  >
                    Visión
                  </h2>
                  <p className="text-sm sm:text-base md:text-lg font-medium leading-relaxed text-gray-900 dark:text-gray-100 text-center">
                    Ser una empresa de paquetería reconocida por su confiabilidad, rapidez y atención al cliente, posicionándose como una de las principales opciones para envíos a nivel local y nacional.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* MISIÓN (DERECHA) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="w-full flex justify-center"
          >
            <div className="mv-outer group">
              <div className="mv-dot" />
              <div className="mv-card">
                <div className="mv-ray" />
                <div className="mv-line mv-topl" />
                <div className="mv-line mv-bottoml" />
                <div className="mv-line mv-leftl" />
                <div className="mv-line mv-rightl" />

                {/* CONTENIDO MISIÓN */}
                <div className="relative z-10 flex flex-col items-center justify-center h-full max-w-md mx-auto">
                  <div className="p-3 rounded-2xl bg-[#CE010B]/10 dark:bg-[#CE010B]/25 text-[#CE010B] dark:text-[#ff4d4d] border border-[#CE010B]/20 shadow-sm mb-4 transition-all duration-300 group-hover:scale-110 group-hover:bg-[#CE010B] group-hover:text-white">
                    <Target className="w-7 h-7" />
                  </div>
                  <h2 
                    className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#CE010B] dark:text-[#ff3b30] tracking-tight mb-4 drop-shadow-sm"
                    style={{ fontFamily: "'Secular One', sans-serif" }}
                  >
                    Misión
                  </h2>
                  <p className="text-sm sm:text-base md:text-lg font-medium leading-relaxed text-gray-900 dark:text-gray-100 text-center">
                    Brindar un servicio de paquetería confiable, seguro y eficiente, facilitando el envío de paquetes de manera rápida y sencilla, con atención cercana y seguimiento constante que genere confianza en cada cliente.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* =========================================================================
          SECCIÓN: VALORES DE LA MARCA
          ========================================================================= */}
      <section className="w-full pt-8 sm:pt-10 pb-16 border-t border-black/10 dark:border-white/10">
        
        {/* ENCABEZADO DE VALORES */}
        <div className="flex flex-col items-center md:items-start text-center md:text-left mb-10 sm:mb-16 px-2 sm:px-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#CE010B] via-[#E60000] to-[#CE010B] dark:from-[#590000] dark:to-[#780000] text-white border border-white dark:border-white/40 shadow-md text-sm font-semibold tracking-wider uppercase mb-3">
            <Sparkles className="w-4 h-4 text-white" /> Principios Fundamentales
          </div>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)]"
            style={{ fontFamily: "'Secular One', sans-serif" }}
          >
            Valores de la marca
          </h2>
        </div>

        {/* GRID DE TARJETAS UIVERSE.IO */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10 justify-items-center w-full max-w-6xl mx-auto">
          {valores.map((val, idx) => (
            <motion.div
              key={val.numero}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 * (idx + 1) }}
              className="w-full flex justify-center"
            >
              <div className="uiverse-card group">
                <div className="uiverse-bg" />
                <div className="uiverse-blob" />
                
                {/* CONTENIDO DE LA TARJETA */}
                <div className="uiverse-card-content">
                  
                  {/* CABECERA: NÚMERO E ÍCONO */}
                  <div className="flex items-center justify-between w-full mb-2 px-1">
                    <span className="text-3xl font-black text-gray-900 dark:text-white tracking-widest transition-transform duration-300 group-hover:scale-110">
                      {val.numero}
                    </span>
                    <div className="p-2.5 rounded-xl bg-[#CE010B]/10 dark:bg-[#CE010B]/25 text-[#CE010B] dark:text-[#ff4d4d] border border-[#CE010B]/20 shadow-sm transition-all duration-300 group-hover:scale-110 group-hover:bg-[#CE010B] group-hover:text-white">
                      <val.icon className="w-5 h-5" />
                    </div>
                  </div>

                  {/* LÍNEA SEPARADORA */}
                  <div className="w-full h-[1px] bg-black/15 dark:bg-white/15 my-2" />

                  {/* TÍTULO DEL VALOR */}
                  <h3 
                    className="text-2xl sm:text-3xl font-bold text-[#CE010B] dark:text-[#ff4d4d] tracking-tight my-2"
                    style={{ fontFamily: "'Secular One', sans-serif" }}
                  >
                    {val.titulo}
                  </h3>

                  {/* DESCRIPCIÓN */}
                  <p className="text-sm sm:text-[15px] font-normal leading-relaxed text-gray-700 dark:text-gray-200 text-center mt-1">
                    {val.descripcion}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </section>

    </div>
  );
}
